/**
 * The one email rule for the whole app.
 *
 * Added 2026-09-07. Before this, ten files each carried their own copy of
 *
 *     /^[^\s@]+@[^\s@]+\.[^\s@]+$/
 *
 * which looks right and accepts `a@b.com).` — `[^\s@]` permits brackets and a
 * trailing full stop. STORE MAKERS INTERIORS' primary contact was saved through
 * one of those forms as
 *
 *     tristan.rodil@storemakers-me.com).
 *
 * a paste that kept the closing bracket. It reached 46 quotation and job-order
 * snapshots, was appended to the To of every status email for that customer, and
 * Gmail refused the whole message with `400 Invalid To header` — so the two
 * VALID recipients on the same mail lost it as well. Two shipments went a
 * fortnight with no booking confirmation while the send retried hourly.
 *
 * Matches `fai_ai.util.email_address` (Python) and `ContactEmailValidation`
 * (Kotlin). If you change one, change all three.
 */

/**
 * RFC 5322 dot-atom local part, a real domain, and a TLD of at least two
 * letters.
 *
 * Deliberately no stricter than the RFC: `d'souza@x.com` and `a+tag@x.com` are
 * legal addresses belonging to real customers, and a "tidier" pattern would
 * reject them at the door. Deliberately no looser either: `a@b` with no TLD is
 * exactly the shape that reaches a mail server and bounces.
 */
export const EMAIL_PATTERN =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/

/** True if a mail server will accept this address as written. */
export function isValidEmail(value: string | null | undefined): boolean {
  if (!value) return false
  return EMAIL_PATTERN.test(value.trim())
}

/** Punctuation a copy-paste leaves clinging to an address — stripped from the ENDS only. */
const EDGE_JUNK = /^[\s"'`<>()[\]{},;:.]+|[\s"'`<>()[\]{},;:.]+$/g

/**
 * Recover a valid address from a value a human clearly meant to be one, or
 * null.
 *
 * Only transformations with exactly one plausible reading are applied —
 * surrounding punctuation, a `mailto:` prefix, a `Name <addr>` wrapper, a comma
 * where the domain dot belongs, a doubled dot. Anything needing a guess
 * (`procurement@sgt`, `logistics@commharbor.com1`) returns null: dropping a
 * recipient is recoverable, inventing a domain nobody verified is not.
 */
export function repairEmail(value: string | null | undefined): string | null {
  if (!value) return null
  let candidate = value.trim()
  if (!candidate) return null
  if (isValidEmail(candidate)) return candidate

  // `Name <addr>`, including the doubled `Name <addr>Name <addr>` form the
  // manual-quote screen used to produce. Take the first bracketed address.
  const angle = candidate.match(/<([^<>]+)>/)
  if (angle) {
    const inner = angle[1].replace(EDGE_JUNK, '')
    if (isValidEmail(inner)) return inner
    candidate = inner
  }

  candidate = candidate.replace(EDGE_JUNK, '')
  if (candidate.toLowerCase().startsWith('mailto:')) {
    candidate = candidate.slice('mailto:'.length).replace(EDGE_JUNK, '')
  }
  if (isValidEmail(candidate)) return candidate

  const at = candidate.lastIndexOf('@')
  if (at > 0) {
    const local = candidate.slice(0, at)
    const domain = candidate.slice(at + 1)
    for (const fixed of [domain.replace(/,/g, '.'), domain.replace(/\.{2,}/g, '.')]) {
      const attempt = `${local}@${fixed}`.replace(EDGE_JUNK, '')
      if (isValidEmail(attempt)) return attempt
    }
  }

  return null
}

/**
 * Validation message for a single address field.
 *
 * Returns the repaired value as a suggestion when there is one, so the form can
 * tell the operator what it thinks they meant instead of just refusing.
 */
export function emailError(value: string): string | null {
  if (isValidEmail(value)) return null
  const suggestion = repairEmail(value)
  return suggestion
    ? `Invalid email format — did you mean ${suggestion}?`
    : 'Invalid email format'
}
